import {
    AccountsColumn,
    AccountsRow,
    AccountStatus,
    FilterGroup,
} from '../components/accounts-overview/accounts-overview.types';
import { IClassificationSummary, IProjectAccount, ReconciliationState } from '../models/project-account.model';
import { IStatementLayout } from '../models/statement-layout.model';

/**
 * The shared vocabulary of the two account screens, and the primitives both need.
 *
 * A PRESENTER turns accounts into what `abcp-accounts-overview` takes. There is one per statement,
 * because the two screens are not one screen with a flag: the balance sheet reconciles documents and
 * shows a status, the income statement analyses figures and shows percentages and subtotals. Every
 * difference between them is a branch that would otherwise sit inside one function, where one
 * screen's change can break the other.
 *
 * What lives HERE is only what is genuinely common: which statement an account belongs to, how a
 * minor-unit amount becomes a displayed one, and how a stored reconciliation state maps to the three
 * the table knows. Everything shaped by a statement lives in that statement's presenter.
 */

/** Which statement an account belongs on. */
export type Statement = 'BALANCE' | 'INCOME';

/** Everything a page hands to `abcp-accounts-overview`, in one object. */
export interface AccountsPresentation {
    readonly columns: AccountsColumn[];
    readonly rows: AccountsRow[];
    readonly filterGroups: FilterGroup[];
    /** Whether the status column is shown at all. False for the income statement. */
    readonly showStatus: boolean;
}

/** What a presenter needs to know. Everything else it derives. */
export interface PresenterInput {
    readonly accounts: readonly IProjectAccount[];
    /** Group ids the user has collapsed. Their headings stay, their rows go. */
    readonly collapsed: ReadonlySet<string>;
    /** The active filter chip, or `all`. Meaning is the presenter's, not this file's. */
    readonly activeFilter: string;
    /**
     * The statement's structure, as the backend sent it. `null` from a backend without layouts: the
     * presenter then lists the positions flat, in catalogue order.
     */
    readonly layout: IStatementLayout | null;
    /** Resolves a position key — or `null` for the unassigned group — to its label. */
    readonly labelOf: (position: string | null) => string;
    /** Resolves a layout label key (group heading, total, subtotal) to its text. */
    readonly layoutLabelOf: (labelKey: string) => string;
    /** The workspace header's search term (REV-22006). Absent or blank means no search. */
    readonly searchTerm?: string;
}

const MINOR_UNITS_PER_UNIT = 100;
const NOTHING = 0;
/** Everything below this number is a balance-sheet account in the Swiss KMU chart. */
const FIRST_INCOME_STATEMENT_CLASS = 3000;
const INCOME_STATEMENT_PREFIX = 'INCOME_STATEMENT_';

/** The group that holds accounts without a position. Its own id, because `null` is not one. */
export const UNASSIGNED_GROUP_ID = 'unassigned';

/**
 * Position keys whose balances come out NEGATIVE from the aggregation.
 *
 * Aggregation is always debits minus credits, so a liability or equity account yields a negative
 * number. Turning that into the positive figure a balance sheet shows is the display's job.
 *
 * Balance-sheet positions only. The income statement has its own rule in `displaySignOf`.
 */
export function isCreditSide(position: string): boolean {
    return position.includes('_LIABILITIES') || position.startsWith('EQUITY_');
}

/**
 * The factor between a stored balance (debit − credit) and the figure the screens show. The one sign
 * rule of the account surface: every table, head and Kontenblatt goes through it, so one account never
 * shows two signs.
 *
 * - Liabilities and equity: −1, the balance sheet shows them positive.
 * - Every income-statement account: −1. The statement shows each line's effect on the result
 *   (Staffelform): revenue positive, expense negative, and the lines add up to the result. One rule
 *   for the whole statement, so a mixed position (Nebenerfolg, ausserordentlich) needs no account type.
 * - Everything else: +1.
 *
 * Without a position the account number decides the statement, as in `statementOf`.
 */
export function displaySignOf(position: string | null, accountNumber?: string): 1 | -1 {
    if (statementOfPosition(position, accountNumber) === 'INCOME') {
        return -1;
    }

    return position !== null && isCreditSide(position) ? -1 : 1;
}

/**
 * The statement an account belongs on.
 *
 * From the POSITION when it has one — the prefix carries it, which is why the position values are
 * named the way they are. An unassigned account has no position, so its number decides, using the
 * same class boundary the vendor import uses. Without that fallback an unassigned account would
 * appear on both screens or on neither, and it is exactly the account the user has to find.
 */
/** What the banner above the account table says, or `null` for no banner at all. */
export interface AccountsBanner {
    readonly variant: 'progress' | 'warning' | 'success';
    readonly messageKey: string;
    readonly params: Record<string, number>;
    /** The action's i18n key, or `null` when the banner asks for nothing. */
    readonly actionKey: string | null;
}

/**
 * The one place that decides which banner the account surface shows.
 *
 * A pure function of four inputs, shared by the balance-sheet and income-statement screens so the two
 * cannot drift — and so the rules below are testable without a page, a store or a template.
 *
 * The order of the cases IS the design:
 *
 *  1. Nothing known yet → no banner. Better silent than a guess while the counters are unread.
 *  2. Overdue wins over everything. A lost classification request must not read as work in progress,
 *     or the surface waits forever and nobody can tell.
 *  3. Work running → progress, and deliberately NO action button. The accounts still waiting are not
 *     a person's job, and offering to assign them invites work that is about to be done anyway.
 *  4. Accounts the classifier gave up on → the only actionable state, and it beats the success
 *     report. That combination is the common case, not the edge: in the measured run the model
 *     touched 20 accounts and abandoned 1.
 *  5. The classifier contributed and nothing is left → report it.
 *  6. Otherwise nothing. Notably when the range table and the trustee covered everything: then no
 *     `CLASSIFIER` binding exists and there is nothing to report.
 */
export function accountsBannerOf(input: {
    readonly summary: IClassificationSummary | null;
    readonly overdue: boolean;
    readonly unassignedCount: number;
    readonly classifierContributed: boolean;
}): AccountsBanner | null {
    if (input.summary === null) {
        return null;
    }

    // THE GROUP HALF ONLY, and the number beside it makes that necessary: the message reads
    // "X von {total} noch offen", and `total` counts ACCOUNTS. Adding both halves gave 21 of 240
    // where nine accounts were actually waiting — halves counted against accounts.
    //
    // The group is also the half this surface RENDERS. A pending function is invisible here: the table
    // shows number, name and figures, and groups the rows by position. So a progress line about
    // functions would report work the reader cannot see the result of.
    //
    // ⚠️ The POLL still watches both halves (`pollClassification$`), and so does the cache guard. This
    // is a decision about what to SAY, not about what to wait for.
    const open = input.summary.groupPending;

    if (input.overdue) {
        return {
            variant: 'warning',
            messageKey: 'abcp.accountSurface.classificationOverdue',
            params: {},
            actionKey: null,
        };
    }

    if (open > NOTHING) {
        return {
            variant: 'progress',
            messageKey: 'abcp.accountSurface.classificationRunning',
            // Closing-wide, like the counters it comes from — the wording says so, so it cannot be
            // read as a claim about the rows of the statement currently on screen.
            params: { open, total: input.summary.total },
            actionKey: null,
        };
    }

    if (input.unassignedCount > NOTHING) {
        return {
            variant: 'warning',
            messageKey: 'abcp.accountSurface.unassignedBanner',
            params: { count: input.unassignedCount },
            actionKey: 'abcp.accountSurface.unassignedBannerAction',
        };
    }

    if (input.classifierContributed) {
        return {
            variant: 'success',
            messageKey: 'abcp.accountSurface.classificationDone',
            params: {},
            actionKey: null,
        };
    }

    return null;
}

/**
 * Did the CLASSIFIER assign anything in this closing — the condition for reporting its work.
 *
 * Closing-wide on purpose, like the banner text it feeds: the counters the surface polls are per
 * closing too, and a per-statement answer would claim something about the visible table that the
 * counters beside it do not support.
 *
 * `CLASSIFIER` and nothing else. `IMPORT` is the KMU range table, which runs inside the sync and did
 * the large majority of the work in the measured run (219 of 240) — reporting that as the agent's
 * doing would be a false claim. `MANUAL` is the trustee, `INHERITED` the predecessor closing.
 *
 * Both halves count: a function the model resolved is its work even where a human set the group.
 */
export function hasClassifierBinding(accounts: readonly IProjectAccount[]): boolean {
    return accounts.some((account) => account.groupSource === 'CLASSIFIER' || account.functionSource === 'CLASSIFIER');
}

export function statementOf(account: IProjectAccount): Statement {
    return statementOfPosition(account.accountGroup, account.accountNumber);
}

function statementOfPosition(position: string | null, accountNumber: string | undefined): Statement {
    if (position !== null) {
        return position.startsWith(INCOME_STATEMENT_PREFIX) ? 'INCOME' : 'BALANCE';
    }

    const leading = /^\s*(\d+)/.exec(accountNumber ?? '');
    const value = leading === null ? NOTHING : Number.parseInt(leading[1] as string, 10);

    return value >= FIRST_INCOME_STATEMENT_CLASS ? 'INCOME' : 'BALANCE';
}

/** `value` times `sign`, never `-0`: a settled account reads 0.00, not -0.00. */
export function inDisplaySign(value: number, sign: 1 | -1): number {
    return value === NOTHING ? NOTHING : sign * value;
}

/** Minor units to the unit the table formats. One place, so no screen divides by a hundred twice. */
/**
 * Whether an account matches the workspace header's search (REV-22006): number, name or amount.
 *
 * The AMOUNT is matched on its rendered decimal form, without sign and without thousands separators,
 * because that is what the user reads and therefore what they type — the same rule the correction
 * register's search follows (`closing-adjustment.selectors.ts`). A blank term matches everything.
 */
export function matchesAccountSearch(account: IProjectAccount, term: string | undefined): boolean {
    const needle = (term ?? '').trim().toLowerCase().replace(/['’]/g, '');
    if (needle.length === NOTHING) {
        return true;
    }
    const amount = Math.abs(toUnits(account.saldoInBaseCurrency.amount)).toFixed(2);

    return [account.accountNumber, account.name, amount].join(' ').toLowerCase().includes(needle);
}

/**
 * The id of the chip that shows or hides accounts which are zero in every period.
 *
 * Shared by both statements, and deliberately NOT one of their filter ids: those change what the
 * loaded rows LOOK like, this one changes which rows are loaded at all. A container has to tell the
 * two apart, because only one of them costs a request.
 */
export const ZERO_ACCOUNTS_CHIP_ID = 'zeroAccounts';

/**
 * The zero-accounts chip. Locked while `lockedReason` is set and the chip is off: accounts without a
 * balance may stay without a position, and showing them before they have one would put them in the
 * statement unplaced. Switched on, it stays usable — the user must be able to turn it off.
 */
export function zeroAccountsChipOf(
    label: string,
    lockedReason: string | null,
    active: boolean,
): { id: string; label: string; active: boolean; disabled?: boolean; reason?: string } {
    return lockedReason !== null && !active
        ? { id: ZERO_ACCOUNTS_CHIP_ID, label, active, disabled: true, reason: lockedReason }
        : { id: ZERO_ACCOUNTS_CHIP_ID, label, active };
}

export function toUnits(amount: number): number {
    return amount / MINOR_UNITS_PER_UNIT;
}

/**
 * REV-22005 — the way back: a number a person typed, in units, to integer MINOR units.
 *
 * ROUNDED, not truncated: `51384.10 * 100` is `5138409.999…` in floating point, and a truncation
 * would store one Rappen less than the trustee typed. The backend refuses anything but an integer, so
 * the rounding has to happen here, once, next to its inverse.
 */
export function toMinor(units: number): number {
    return Math.round(units * MINOR_UNITS_PER_UNIT);
}

/** The three display states from the stored ones — the mapping the backend deliberately does not do. */
export function statusOf(state: ReconciliationState): AccountStatus {
    switch (state) {
        case 'DIFFERENCE':
            return 'difference';
        case 'MATCHED':
            return 'reconciled';
        // A hand-entered value without a verdict: the backend compares an entry with the ERP balance at
        // once (MATCHED or DIFFERENCE), so this is the value after an ERP sync, or one whose balance
        // could not be read — open, as the kernel's `displayStatus()` says. Named rather than left to
        // `default`, so the choice is visible.
        case 'MACHINE_DISCARDED':
            return 'open';
        default:
            return 'open';
    }
}

/**
 * The document value minus the ERP balance, in minor units and the ledger sign — computed here, the
 * backend no longer sends it. `null` without a document value, and when the two are in different
 * currencies: a difference across currencies says nothing.
 */
export function documentDifferenceOf(
    account: Pick<IProjectAccount, 'documentBalance' | 'saldoInBaseCurrency'> &
        Partial<Pick<IProjectAccount, 'accountFunction'>>,
): { amountMinor: number; currency: string } | null {
    const document = account.documentBalance;
    const erp = account.saldoInBaseCurrency;
    if (document == null || erp == null || document.currency !== erp.currency) {
        return null;
    }
    // Delkredere is compared in absolute amount (the sheet shows the allowance positive, the account
    // carries it as a credit): the card must show the difference the comparison measured, not the
    // signed one, or a reconciled account would show a figure twice its balance (A5, 20.10.2026).
    if (account.accountFunction === ABSOLUTE_COMPARISON_FUNCTION) {
        return { amountMinor: Math.abs(document.amountMinor) - Math.abs(erp.amount), currency: document.currency };
    }
    return { amountMinor: document.amountMinor - erp.amount, currency: document.currency };
}

/** The one function the kernel's rule table compares «Gleich im Betrag» (`FUNCTION_RULES`, comparison ABSOLUTE). */
const ABSOLUTE_COMPARISON_FUNCTION = 'ALLOWANCE_TRADE_RECEIVABLES';

/** Account number order, numerically — `1000` before `1100`, and `9` before `10`. */
export function byAccountNumber(left: IProjectAccount, right: IProjectAccount): number {
    return left.accountNumber.localeCompare(right.accountNumber, undefined, { numeric: true });
}

/**
 * The accounts of one statement, bucketed by position and ordered as the statement is read.
 *
 * A statement is read top to bottom in a fixed sequence, not alphabetically. Accounts without a
 * position come last, under their own heading: they are the ones needing attention, and a heading the
 * user can see beats a silent omission.
 */
export function groupByPosition(
    accounts: readonly IProjectAccount[],
    statement: Statement,
    positionOrder: readonly string[],
): { readonly position: string | null; readonly accounts: IProjectAccount[] }[] {
    const buckets = new Map<string | null, IProjectAccount[]>();

    for (const account of accounts.filter((candidate) => statementOf(candidate) === statement)) {
        const bucket = buckets.get(account.accountGroup);
        if (bucket === undefined) {
            buckets.set(account.accountGroup, [account]);
        } else {
            bucket.push(account);
        }
    }

    const ordered: (string | null)[] = positionOrder.filter((position) => buckets.has(position));
    if (buckets.has(null)) {
        ordered.push(null);
    }

    return ordered.map((position) => ({
        position,
        accounts: (buckets.get(position) ?? []).sort(byAccountNumber),
    }));
}

/**
 * The accounts a statement can place and the ones it cannot: no position, no place in it. Accounts
 * without a balance may stay unpositioned (REV-22504), so one can reach the list when the user asks
 * for the zero accounts or picks a period they have figures in — the screen then says which ones.
 */
export function splitUnpositioned(accounts: readonly IProjectAccount[]): {
    readonly positioned: IProjectAccount[];
    readonly unpositioned: IProjectAccount[];
} {
    const positioned: IProjectAccount[] = [];
    const unpositioned: IProjectAccount[] = [];
    for (const account of accounts) {
        (account.accountGroup === null ? unpositioned : positioned).push(account);
    }

    return { positioned, unpositioned };
}

const UNPOSITIONED_NAMED = 8;

/** «1021 Bank, 1022 Kasse … (+3)» — the first few accounts, then how many more there are. */
export function unpositionedLabelOf(accounts: readonly IProjectAccount[]): string {
    const named = accounts
        .slice(0, UNPOSITIONED_NAMED)
        .map((account) => `${account.accountNumber} ${account.name}`)
        .join(', ');
    const more = accounts.length - UNPOSITIONED_NAMED;

    return more > 0 ? `${named} … (+${String(more)})` : named;
}

/** How many accounts, zero accounts included, still lack a position. Zero when the counters are unknown. */
export function accountsWithoutPositionCount(summary: { total: number; groupClassified: number } | null): number {
    return summary === null ? 0 : Math.max(0, summary.total - summary.groupClassified);
}
